import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import { OptionStepper } from "../shared/selection-nav.component.tsx";
import { Select } from "../shared/select.component.tsx";
import type { MediaFile, MediaFileType } from "./patient-media.types.ts";
import {
  getPatientFiles,
  formatFileSize,
  formatFileDate,
} from "./patient-media-previews.ts";
import { usePatientMedia } from "./patient-media.provider.tsx";

// ── SVG thumbnail generators ────────────────────────────────────────────────

function hashId(id: string): number {
  return id.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0);
}

function jpegThumb(id: string): string {
  const h = hashId(id);
  const isIntraoral = id.includes("Intrao") || id.includes("Occlusal");
  const isProfile = id.includes("Profil");
  const skinTone = ["#f5dcc8", "#f0c8a8", "#e8b888", "#fce8d8"][h % 4]!;
  const eyeColor = ["#3d2b1a", "#2d4a7a", "#3d6b3d"][h % 3]!;

  if (isIntraoral) {
    return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
      <rect width='160' height='120' fill='#e8d5c4'/>
      <ellipse cx='80' cy='70' rx='55' ry='32' fill='#c8a080'/>
      <ellipse cx='80' cy='55' rx='50' ry='18' fill='#f0e8d0'/>
      ${[...Array(7)]
        .map((_, i) => {
          const x = 25 + i * 16;
          const tw = 13 - Math.abs(i - 3) * 1.5;
          const th = 12 + (i === 0 || i === 6 ? 0 : 4);
          return `<rect x='${x}' y='${42}' width='${tw}' height='${th}' rx='3' fill='white' stroke='#d0c8a0' stroke-width='0.8'/>`;
        })
        .join("")}
      ${[...Array(7)]
        .map((_, i) => {
          const x = 25 + i * 16;
          const tw = 13 - Math.abs(i - 3) * 1.5;
          const th = 11 + (i === 0 || i === 6 ? 0 : 3);
          return `<rect x='${x}' y='${62}' width='${tw}' height='${th}' rx='3' fill='white' stroke='#d0c8a0' stroke-width='0.8'/>`;
        })
        .join("")}
      <ellipse cx='80' cy='55' rx='55' ry='15' fill='none' stroke='#c8b090' stroke-width='1'/>
      <rect x='0' y='90' width='160' height='30' fill='rgba(0,0,0,0.3)'/>
      <text x='80' y='109' font-size='9' fill='white' text-anchor='middle' font-family='sans-serif'>${isIntraoral ? "Vue occlusale" : "Intraoral"}</text>
    </svg>`;
  }

  const cx = isProfile ? 95 : 80;
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
    <rect width='160' height='120' fill='${["#f0f4f8", "#e8f0e8", "#f4f0f8"][h % 3]}'/>
    <ellipse cx='${cx}' cy='48' rx='${isProfile ? 22 : 26}' ry='30' fill='${skinTone}'/>
    <ellipse cx='${cx}' cy='90' rx='${isProfile ? 30 : 40}' ry='32' fill='${skinTone}'/>
    ${
      isProfile
        ? `
      <ellipse cx='108' cy='44' rx='7' ry='8' fill='white'/>
      <circle cx='108' cy='45' r='4' fill='${eyeColor}'/>
      <path d='M97 58 Q105 63 113 58' stroke='#b06040' stroke-width='1.5' fill='none' stroke-linecap='round'/>
      <ellipse cx='100' cy='38' rx='4' ry='2' fill='${skinTone}' stroke='#a08060' stroke-width='0.8'/>
    `
        : `
      <ellipse cx='68' cy='44' rx='7' ry='8' fill='white'/><ellipse cx='92' cy='44' rx='7' ry='8' fill='white'/>
      <circle cx='68' cy='45' r='4' fill='${eyeColor}'/><circle cx='92' cy='45' r='4' fill='${eyeColor}'/>
      <circle cx='69.5' cy='43.5' r='1.5' fill='white'/><circle cx='93.5' cy='43.5' r='1.5' fill='white'/>
      <path d='M70 58 Q80 64 90 58' stroke='#b06040' stroke-width='1.5' fill='none' stroke-linecap='round'/>
    `
    }
    <rect x='0' y='90' width='160' height='30' fill='rgba(0,0,0,0.35)'/>
    <text x='80' y='109' font-size='9' fill='white' text-anchor='middle' font-family='sans-serif'>${isProfile ? "Vue de profil" : "Vue de face"}</text>
  </svg>`;
}

function pngThumb(id: string): string {
  const isPanoramic = id.includes("pan") || id.includes("Pan");
  const numTeeth = isPanoramic ? 14 : 8;
  const spacing = isPanoramic ? 10 : 17;
  const startX = isPanoramic ? 5 : 10;
  const teeth = [...Array(numTeeth)]
    .map((_, i) => {
      const x = startX + i * spacing;
      const th = 28 + (i % 4) * 8;
      return `<rect x='${x}' y='${52 - th / 2}' width='${spacing - 2}' height='${th}' rx='4' fill='#7ab8e8' opacity='0.65'/>
            <ellipse cx='${x + (spacing - 2) / 2}' cy='${52 - th / 2 + 5}' rx='${(spacing - 3) / 2}' ry='3' fill='#a0d0f8' opacity='0.5'/>
            <ellipse cx='${x + (spacing - 2) / 2}' cy='${52 + th / 2 - 5}' rx='${(spacing - 3) / 2}' ry='3' fill='#a0d0f8' opacity='0.5'/>`;
    })
    .join("");
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
    <rect width='160' height='120' fill='#070d1a'/>
    <g opacity='0.8'>${teeth}</g>
    <rect x='0' y='88' width='160' height='32' fill='#040a14'/>
    <text x='80' y='108' font-size='8' fill='#4a90c0' text-anchor='middle' font-family='monospace'>${isPanoramic ? "PANORAMIQUE · 2024" : "TÉLÉRADIOGRAPHIE · 2024"}</text>
    <rect x='132' y='6' width='22' height='10' rx='2' fill='#0a2040' stroke='#1a4060' stroke-width='0.5'/>
    <text x='143' y='13.5' font-size='6' fill='#4a8ab0' text-anchor='middle' font-family='monospace'>DR M.</text>
  </svg>`;
}

function stlThumb(id: string): string {
  const isMaxi =
    id.includes("maxi") || id.includes("Maxi") || id.includes("max");
  const n = isMaxi ? 14 : 12;
  const teeth = [...Array(n)]
    .map((_, i) => {
      const t = (i / (n - 1)) * Math.PI;
      const cx = 80 + Math.cos(t - Math.PI / 2) * 58;
      const cy = 62 + Math.sin(t - Math.PI / 2) * 22;
      const th = 14 + (i % 3) * 4;
      return `<ellipse cx='${cx}' cy='${cy}' rx='5' ry='${th / 2}' fill='#7c3aed' stroke='#a78bfa' stroke-width='0.8'/>
            <ellipse cx='${cx}' cy='${cy - th / 2 + 3}' rx='3.5' ry='2' fill='#8b5cf6' opacity='0.8'/>`;
    })
    .join("");
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
    <defs>
      <linearGradient id='sg' x1='0%' y1='0%' x2='100%' y2='100%'><stop offset='0%' stop-color='#7c3aed'/><stop offset='100%' stop-color='#4c1d95'/></linearGradient>
    </defs>
    <rect width='160' height='120' fill='#13103a'/>
    ${[...Array(5)].map((_, i) => `<line x1='10' y1='${30 + i * 18}' x2='150' y2='${30 + i * 18}' stroke='#2d2a6a' stroke-width='0.5'/>`).join("")}
    ${[...Array(8)].map((_, i) => `<line x1='${10 + i * 20}' y1='30' x2='${10 + i * 20}' y2='120' stroke='#2d2a6a' stroke-width='0.5'/>`).join("")}
    <ellipse cx='80' cy='62' rx='62' ry='18' fill='url(#sg)' opacity='0.85'/>
    <ellipse cx='80' cy='62' rx='62' ry='18' fill='none' stroke='#a78bfa' stroke-width='1.5' opacity='0.6'/>
    ${teeth}
    <text x='80' y='113' font-size='8' fill='#7c3aed' text-anchor='middle' font-family='monospace'>${isMaxi ? "ARCADE MAXILLAIRE" : "ARCADE MANDIBULAIRE"}</text>
  </svg>`;
}

function mp4Thumb(): string {
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
    <defs>
      <linearGradient id='vg' x1='0%' y1='0%' x2='100%' y2='100%'><stop offset='0%' stop-color='#0f2942'/><stop offset='100%' stop-color='#1a3a5c'/></linearGradient>
    </defs>
    <rect width='160' height='120' fill='url(#vg)'/>
    <rect x='0' y='88' width='160' height='32' fill='rgba(0,0,0,0.6)'/>
    <rect x='8' y='94' width='85' height='3' rx='1.5' fill='rgba(255,255,255,0.2)'/>
    <rect x='8' y='94' width='30' height='3' rx='1.5' fill='#22d3ee'/>
    <text x='150' y='99' font-size='8' fill='rgba(255,255,255,0.5)' text-anchor='end' font-family='monospace'>1:38</text>
    <circle cx='80' cy='48' r='22' fill='rgba(255,255,255,0.12)'/>
    <circle cx='80' cy='48' r='16' fill='rgba(255,255,255,0.2)'/>
    <polygon points='74,40 74,56 94,48' fill='white' opacity='0.9'/>
  </svg>`;
}

function pdfThumb(name: string): string {
  const label = name.replace(/-/g, " ").replace(/_/g, " ").slice(0, 24);
  return `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 120'>
    <rect width='160' height='120' fill='#f8fafc'/>
    <rect x='20' y='0' width='120' height='120' fill='white' stroke='#e2e8f0' stroke-width='1'/>
    <rect x='20' y='0' width='120' height='18' fill='#e53e3e'/>
    <text x='80' y='13' font-size='9' font-weight='bold' fill='white' text-anchor='middle' font-family='sans-serif'>PDF</text>
    <text x='30' y='36' font-size='7' fill='#1a202c' font-weight='600' font-family='sans-serif'>${label}</text>
    ${[0.9, 0.7, 0.85, 0.6, 0.75, 0.5, 0.65, 0.4]
      .map(
        (w, i) =>
          `<rect x='30' y='${44 + i * 7}' width='${w * 100}' height='3' rx='1' fill='#cbd5e0'/>`,
      )
      .join("")}
    <rect x='30' y='102' width='100' height='10' rx='2' fill='#f0f4f8' stroke='#e2e8f0' stroke-width='0.5'/>
  </svg>`;
}

// ── Type metadata ────────────────────────────────────────────────────────────
const TYPE_META: Record<
  MediaFileType,
  { label: string; color: string; textColor: string }
> = {
  jpeg: { label: "JPEG", color: "#dcfce7", textColor: "#15803d" },
  png: { label: "PNG", color: "#1e293b", textColor: "#7ab8e8" },
  stl: { label: "STL", color: "#ede9fe", textColor: "#6d28d9" },
  mp4: { label: "MP4", color: "#0f172a", textColor: "#22d3ee" },
  pdf: { label: "PDF", color: "#fee2e2", textColor: "#dc2626" },
};

// ── Single file thumbnail card ───────────────────────────────────────────────
function FileThumbnail({
  file,
  selected,
  thumbW,
}: {
  file: MediaFile;
  selected: boolean;
  thumbW: number;
}) {
  const svgContent = React.useMemo(() => {
    if (file.type === "jpeg") return jpegThumb(file.id);
    if (file.type === "png") return pngThumb(file.id);
    if (file.type === "stl") return stlThumb(file.id);
    if (file.type === "mp4") return mp4Thumb();
    if (file.type === "pdf") return pdfThumb(file.name);
    return null;
  }, [file.id, file.type, file.name]);

  const meta = TYPE_META[file.type];
  const thumbH = Math.round(thumbW * 0.75);

  return (
    <div
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border-2 bg-white transition-all duration-150 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
        selected
          ? "border-sf-primary shadow-[0_0_0_3px_var(--sf-primary-50)]"
          : "border-sf-border hover:border-sf-primary/40",
      )}
    >
      {/* Thumbnail area */}
      <div className="shrink-0 overflow-hidden" style={{ height: thumbH }}>
        {svgContent ? (
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svgContent)}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-sf-surface-alt">
            <I.FileText size={32} className="text-sf-text-muted" />
          </div>
        )}
      </div>

      {/* Info strip */}
      <div className="flex min-w-0 items-center gap-1.5 px-2 py-1.5">
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[9px] font-bold"
          style={{ background: meta.color, color: meta.textColor }}
        >
          {meta.label}
        </span>
        <span className="truncate text-[11px] font-medium text-sf-text">
          {file.name}
        </span>
      </div>

      {/* Selected overlay check */}
      {selected && (
        <div className="pointer-events-none absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-sf-primary shadow-sm">
          <I.Check size={11} className="text-white" />
        </div>
      )}
    </div>
  );
}

// ── Widget ───────────────────────────────────────────────────────────────────
export function PatientFilesWidget() {
  const { current } = Interactions.usePatient();
  const { selectFile } = usePatientMedia();

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [thumbSize, setThumbSize] = React.useState<"sm" | "md" | "lg">("md");
  const [sortBy, setSortBy] = React.useState<"type" | "date" | "name">("type");

  const files = React.useMemo(
    () => (current ? getPatientFiles(current.uuid) : []),
    [current?.uuid],
  );

  const sortedFiles = React.useMemo(() => {
    const f = [...files];
    if (sortBy === "date") f.sort((a, b) => b.date.localeCompare(a.date));
    else if (sortBy === "name") f.sort((a, b) => a.name.localeCompare(b.name));
    return f;
  }, [files, sortBy]);

  const thumbW = thumbSize === "sm" ? 88 : thumbSize === "md" ? 120 : 160;
  const colClass =
    thumbSize === "sm"
      ? "grid-cols-[repeat(auto-fill,minmax(88px,1fr))]"
      : thumbSize === "md"
        ? "grid-cols-[repeat(auto-fill,minmax(120px,1fr))]"
        : "grid-cols-[repeat(auto-fill,minmax(160px,1fr))]";

  if (!current) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-center">
        <I.FolderOpen size={22} className="text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  const handleClick = (f: MediaFile) => {
    setSelectedId(f.id);
    selectFile(f);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex shrink-0 items-center gap-2 border-b border-sf-border bg-sf-surface-alt px-2.5 py-1.5">
        <span className="text-[11px] font-medium text-sf-text-muted">
          {files.length} fichier{files.length > 1 ? "s" : ""}
        </span>
        <div className="flex-1" />

        {/* Sort */}
        <Select
          value={sortBy}
          onChange={(v) => setSortBy(v as typeof sortBy)}
          options={[
            { value: "type", label: "Par type" },
            { value: "date", label: "Par date" },
            { value: "name", label: "Par nom" },
          ]}
          className="w-32"
        />

        <OptionStepper
          ariaLabel="Taille des vignettes"
          value={thumbSize}
          onChange={(v) => setThumbSize(v as "sm" | "md" | "lg")}
          options={[
            { value: "sm", label: "S" },
            { value: "md", label: "M" },
            { value: "lg", label: "L" },
          ]}
        />

        <button
          type="button"
          className="inline-flex h-7 items-center gap-1 rounded border border-sf-primary bg-sf-primary px-2.5 text-[11px] font-semibold text-white"
        >
          <I.Upload size={11} />
          Importer
        </button>
      </div>

      {/* Gallery grid */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
        {sortedFiles.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[12.5px] text-sf-text-muted">
            Aucun fichier
          </div>
        ) : (
          <div className={cn("grid gap-2", colClass)}>
            {sortedFiles.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => handleClick(f)}
                className="text-left focus:outline-none"
                title={`${f.name} · ${formatFileSize(f.size)} · ${formatFileDate(f.date)}`}
              >
                <FileThumbnail
                  file={f}
                  selected={selectedId === f.id}
                  thumbW={thumbW}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status bar */}
      {selectedId &&
        (() => {
          const f = sortedFiles.find((x) => x.id === selectedId);
          return f ? (
            <div className="flex shrink-0 items-center gap-2 border-t border-sf-border bg-sf-surface-alt px-3 py-1">
              <span className="truncate text-[11px] font-medium text-sf-text">
                {f.name}
              </span>
              <span className="shrink-0 text-[11px] text-sf-text-muted">
                {formatFileSize(f.size)}
              </span>
              <span className="shrink-0 text-[11px] text-sf-text-muted">
                {formatFileDate(f.date)}
              </span>
            </div>
          ) : null;
        })()}
    </div>
  );
}
